import type {
  SCOrderListCountResponseType,
  SCOrderListParametersType,
  SCOrderListResponseType,
} from "@sellar-io/api-v2/types";
import { useFirebaseUser } from "../../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../useSellarCurrentBusiness.shared";
import {
  apiV2AssertResponse,
  ApiV2InfiniteQueryOptions,
  ApiV2QueryOptions,
  apiV2Request,
  getDefaultInfiniteQueryOptionsV2,
  getDefaultQueryOptionsV2,
} from "../apiV2Request";
import { ServerStateKeys } from "../ServerStateKeys";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";

export const useSCOrderList = <TData = SCOrderListResponseType,>(
  params: SCOrderListParametersType,
  options?: ApiV2InfiniteQueryOptions<TData>,
) => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();

  return useInfiniteQuery({
    queryKey: [user?.uid, business?.id, ServerStateKeys.SCOrders, params],
    queryFn: (queryParams) =>
      apiV2Request({
        user,
        business,
        method: "get",
        path: `sellar-credit-management/orders`,
        params: {
          ...params,
          offset: queryParams.pageParam,
        },
      }).then((data) => apiV2AssertResponse<TData>(data).data),
    ...getDefaultInfiniteQueryOptionsV2({
      context: { scope: "business", user, business: business },
      options: { ...options, limit: params.limit },
    }),
  });
};

export const useSCOrderListPaginated = <TData = SCOrderListResponseType,>(
  params: SCOrderListParametersType,
  options?: ApiV2QueryOptions<TData>,
) => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();

  return useQuery({
    queryKey: [user?.uid, business?.id, ServerStateKeys.SCOrders, params],
    queryFn: () =>
      apiV2Request({
        user,
        business,
        method: "get",
        path: `sellar-credit-management/orders`,
        params,
      }).then((data) => apiV2AssertResponse<TData>(data).data),
    ...getDefaultQueryOptionsV2({
      context: { scope: "business", user, business: business },
      options: { ...options },
    }),
  });
};

export const useSCOrderListCount = <TData = SCOrderListCountResponseType,>(
  params: SCOrderListParametersType,
  options?: ApiV2QueryOptions<TData>,
) => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();

  return useQuery({
    queryKey: [
      user?.uid,
      business?.id,
      ServerStateKeys.SCOrders,
      params,
      "count",
    ],
    queryFn: () =>
      apiV2Request({
        user,
        business,
        method: "get",
        path: `sellar-credit-management/orders/count`,
        params,
      }).then((data) => apiV2AssertResponse<TData>(data).data),
    ...getDefaultQueryOptionsV2({
      context: { scope: "business", user, business: business },
      options: { ...options },
    }),
  });
};
